/**
 * This is a temporary container-class for dynamic asset objects such as areas, volumes
 * lines and points.
 * The class will most likely be broken down later on. But when?
 */
'use strict';

import { t as translateLiteral } from 'i18next';
import PubSub from 'pubsub-js';
import * as THREE from 'three';
import { AmbientLight, Camera, Mesh, Object3D, Scene, WebGLRenderer } from 'three';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer';
import { CSS3DObject, CSS3DRenderer } from 'three/examples/jsm/renderers/CSS3DRenderer';

import {
  CadFile,
  DynamicAssetModel,
  DynamicAssetType,
  FileModel,
} from '../../../typings/api/skymap/rest/v0/.common';
import { GetCadFilesResponse } from '../../../typings/api/skymap/rest/v0/project';
import {
  DynamicAssetCommonPropertiesModel,
  PoiModel,
} from '../../../typings/api/skymap/rest/v1/.common';
import { PoiPatchPayload } from '../../../typings/api/skymap/rest/v1/poi';
import {
  getAssetVolumeCalculationDataType,
  VolumeWithAsset,
} from '../react/components/skyview-new/components/report-tool-menu/helpers/utils';
import { projectStateAdapter } from '../react/state/project-state/project-state-adapter';
import { getFileExtension } from '../react/utils/path';
import { SkyMapAxiosServiceFactory } from '../services/axios/skymap-axios-service-factory';
import { WebApi } from '../services/web-api';
import { iiafe } from '../utils/async';
import { FeatureFlagsManager } from '../utils/core/feature-flags-manager';
import { publishNotification } from '../utils/notifications/publish-notification';
import { assertNever, isDefined, toArray } from '../utils/variables';
import { AssetVisibilityManager } from './asset-visibility-manager';
import { SkyviewCamera } from './cameras/types';
import { Cluster } from './elements/cluster';
import { Design } from './elements/design';
import { Dxf } from './elements/dxf';
import { AssetCategory, AssetSaveState, AssetType, ElementObj } from './elements/element-obj';
import { AssetWithFileInfo } from './elements/file-info/types';
import { Geometry } from './elements/geometry';
import { Glb } from './elements/glb';
import { Model } from './elements/model';
import { Poi, PoiProperties } from './elements/poi/poi';
import { TerrainModel } from './elements/terrain-model';
import { CellGridVolume } from './elements/volume/cell-grid-volume';
import { CellGridVolumeModel } from './elements/volume/model/cell-grid-volume-model';
import { PrismVolumeModel } from './elements/volume/model/prism-volume-model';
import { PrismVolume } from './elements/volume/prism-volume';
import { VolumeCalculationAsset, VolumeCalculationAssetTypeGuard } from './elements/volume/types';
import { getCompletedVolumes } from './elements/volume/utils';
import { OrthoTileAsset } from './geodata/ortho-tile-asset/ortho-tile-asset';
import { InputHandler } from './input-handler';
import { AssetFactory } from './loader/asset/asset-factory';
import { DxfFactory } from './loader/dxf/dxf-factory';
import { GltfLoader } from './loader/gltf';
import { publish, subscribe, SubscriptionTopic, unsubscribe } from './messaging/pubsub';
import { ProjectGeodataManager } from './project-geodata-manager';
import { SunManager } from './sun-manager';
import { disposeRecursive } from './utilities/three/dispose';
import { configureTexts } from './utilities/three/text-builder';

type AssetTypeToModel<T> = T extends 'area'
  ? Geometry[]
  : T extends 'line'
    ? Geometry[]
    : T extends 'design'
      ? Design[]
      : T extends 'dxf'
        ? Dxf[]
        : T extends 'glb'
          ? Glb[]
          : T extends 'model'
            ? Model[]
            : T extends 'terrain-model'
              ? TerrainModel[]
              : T extends 'poi'
                ? Poi[]
                : never;

/**
 * The most file versions that one request converts, see convertDrawingFiles.
 */
export const maxConvertedFileVersions = 100;

export class AssetManager extends THREE.Scene {
  static get instance() {
    return AssetManager._instance;
  }

  private static _instance: AssetManager = new AssetManager();

  private prevCadAssetPathMap = new Map<string, string>();

  private viewer?: { skyview?: { dispatchEvent(event: { type: string }): void } };

  private AxiosProjectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV0();
  private StaticAssetService = SkyMapAxiosServiceFactory.instance.createStaticAssetServiceV0();
  private SharedViewService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();

  private projectionUpdatedSubscription?: string;
  private refreshAssetListSubscription?: string;

  private readOnly: boolean;
  private loading: boolean;

  private selected?: ElementObj;
  private temp: Object3D[];
  tempObj: Scene;

  sharedViewId?: string;
  private projectId?: string;
  private sharedViewAssetVisibilities = new Map<string, boolean>();

  private assetTypeVisibility: { [key in AssetType]: boolean };
  private textScene = new Scene();
  private cssLabelsToRemove: CSS3DObject[] = [];

  // Asset list state that holds data about which categories/cad groups are expanded/visible.
  // TODO: Can be moved into React Context when SkyView is fully implemented with React.
  private assetListState = {
    categories: {
      expanded: new Map<AssetCategory, undefined | boolean>(),
      visible: new Map<AssetCategory, undefined | boolean>(),
    },
    cadGroups: {
      expanded: new Map<string, undefined | boolean>(),
      visible: new Map<string, undefined | boolean>(),
    },
  };

  private constructor() {
    super();

    this.viewer = undefined;

    this.readOnly = false;
    this.loading = false;

    this.selected = undefined;
    this.temp = [];
    this.tempObj = new Scene();
    this.textScene.add(new AmbientLight());

    this.visible = true;

    this.assetTypeVisibility = {
      dxf: true,
      line: true,
      area: true,
      model: true,
      design: true,
      glb: true,
      'terrain-model': false,
      poi: false,
    };

    configureTexts();
  }

  /**
   * Returns a value indicating whether the specified asset category is expanded. If undefined
   * is returned the category has not been explicitly expanded/collapsed.
   */
  isCategoryExpanded(category: AssetCategory) {
    return this.assetListState.categories.expanded.has(category)
      ? this.assetListState.categories.expanded.get(category)!
      : undefined;
  }

  /**
   * Returns a value indicating whether the specified asset category is visible. If undefined
   * is returned the category has not been explicitly shown/hidden.
   */
  isCategoryVisible(category: AssetCategory) {
    return this.assetListState.categories.visible.has(category)
      ? this.assetListState.categories.visible.get(category)!
      : undefined;
  }

  /**
   * Returns a value indicating whether the specified asset cad group is expanded. If undefined
   * is returned the cad group has not been explicitly expanded/collapsed.
   */
  isCadGroupExpanded(cadGroupId: string) {
    return this.assetListState.cadGroups.expanded.has(cadGroupId)
      ? this.assetListState.cadGroups.expanded.get(cadGroupId)!
      : undefined;
  }

  /**
   * Returns a value indicating whether the specified asset cad group is visible. If undefined
   * is returned the cad group has not been explicitly shown/hidden.
   */
  isCadGroupVisible(cadGroupId: string) {
    return this.assetListState.cadGroups.visible.has(cadGroupId)
      ? this.assetListState.cadGroups.visible.get(cadGroupId)!
      : undefined;
  }

  saveAssetCategoryExpanded(category: AssetCategory, expanded: boolean) {
    this.assetListState.categories.expanded.set(category, expanded);
  }

  saveAssetCategoryVisible(category: AssetCategory, visible: boolean) {
    this.assetListState.categories.visible.set(category, visible);
  }

  saveCadGroupExpanded(cadGroupId: string, expanded: boolean) {
    this.assetListState.cadGroups.expanded.set(cadGroupId, expanded);
  }

  saveCadGroupVisible(cadGroupId: string, visible: boolean) {
    this.assetListState.cadGroups.visible.set(cadGroupId, visible);
  }

  setSharedViewAssetVisibilities(assets: { id: string; visible: boolean }[]): void {
    this.sharedViewAssetVisibilities.clear();
    for (const asset of assets) {
      this.sharedViewAssetVisibilities.set(asset.id, asset.visible);
    }
  }

  initialise(
    viewer: { skyview?: { dispatchEvent(event: { type: string }): void } },
    readOnly = false,
    sharedViewId?: string,
  ) {
    this.assetListState.cadGroups.expanded.clear();
    this.assetListState.cadGroups.visible.clear();
    this.assetListState.categories.expanded.clear();
    this.assetListState.categories.visible.clear();
    this.sharedViewAssetVisibilities.clear();

    this.viewer = viewer;
    this.projectId = undefined;

    this.AxiosProjectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV0();
    this.StaticAssetService = SkyMapAxiosServiceFactory.instance.createStaticAssetServiceV0();
    this.SharedViewService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();

    // todo: this.readOnly should be removed when deleting "Publish project" functionality.
    // todo: (this.sharedViewId replaces this.readOnly)
    this.readOnly = readOnly;
    this.sharedViewId = sharedViewId;

    DxfFactory.instance().initialise(
      SkyMapAxiosServiceFactory.instance.createDynamicAssetServiceV0(),
      this.SharedViewService,
    );

    if (isDefined(this.projectionUpdatedSubscription)) {
      unsubscribe(this.projectionUpdatedSubscription);
    }
    this.projectionUpdatedSubscription = subscribe(SubscriptionTopic.OnProjectionUpdated, () => {
      this.calculatePositions(this.children);
      this.calculatePositions(this.tempObj.children);
      InputHandler.instance.currentTool()?.onProjectProjectionUpdated();
    });

    if (isDefined(this.refreshAssetListSubscription)) {
      unsubscribe(this.refreshAssetListSubscription);
    }
    this.refreshAssetListSubscription = subscribe(SubscriptionTopic.RefreshAssetList, (data) => {
      iiafe(async () => {
        await AssetManager.instance.loadProjectAssets(data.projectId, data.assetTypes);
      });
    });
  }

  /**
   * @description Set if the scene is to be visible
   * @param {boolean} state
   */
  setVisible(state: boolean) {
    this.visible = state;
  }

  private async fetchDynamicAssets(
    projectId: string,
  ): Promise<{ assets: (DynamicAssetModel | PoiModel)[]; cadFilesResponse: GetCadFilesResponse }> {
    const projectServiceV1 = SkyMapAxiosServiceFactory.instance.createProjectServiceV1();

    const getCadFiles = async () => {
      return this.sharedViewId
        ? await this.SharedViewService.getCadFilesV1({ path: { id: this.sharedViewId } })
        : await SkyMapAxiosServiceFactory.instance.createProjectServiceV0().getCadFilesV1({
            path: { projectId },
          });
    };

    const getPoiAssets = async () => {
      return this.sharedViewId
        ? (
            await SkyMapAxiosServiceFactory.instance.createSharedViewServiceV1().getPois({
              path: { sharedViewId: this.sharedViewId },
              query: {
                include: [
                  'poi.updatedBy',
                  'poi.thumbnailImage',
                  'poi.timelineContainer',
                  'poi.createdBy',
                ],
              },
            })
          ).data
        : (
            await projectServiceV1.getPois({
              path: { projectId },
              query: {
                include: [
                  'poi.updatedBy',
                  'poi.thumbnailImage',
                  'poi.timelineContainer',
                  'poi.assetTags',
                  'poi.createdBy',
                ],
              },
            })
          ).data;
    };

    const getProjectAssets = async () => {
      const sharedViewService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();
      const assets = this.sharedViewId
        ? (await sharedViewService.getAssets({ path: { id: this.sharedViewId } })).data
        : (await this.AxiosProjectService.getProjectAssets({ path: { projectId } })).data;

      return assets.filter((asset) => asset.type !== 'Comment');
    };

    const [assets, poiAssets, cadFilesResponse] = await Promise.all([
      getProjectAssets(),
      getPoiAssets(),
      getCadFiles(),
    ]);

    return {
      assets: [...assets, ...poiAssets],
      cadFilesResponse,
    };
  }

  async fetchStaticAssets(projectId: string) {
    return this.sharedViewId
      ? (await this.SharedViewService.getModels({ path: { id: this.sharedViewId } })).data
      : (await this.AxiosProjectService.getProjectModels({ path: { projectId } })).data;
  }

  async refreshDynamicAssetsById(projectId: string, assetIds: string[]) {
    const { assets, cadFilesResponse } = await this.fetchDynamicAssets(projectId);
    const filteredAssets = assets.filter((asset) => assetIds.includes(asset.id));
    await this.refreshDynamicAssets(filteredAssets, cadFilesResponse);
    this.publishAssetListChanged();
  }

  private async refreshAllDynamicAssets(
    projectId: string,
    types?: (DynamicAssetType | 'Poi') | (DynamicAssetType | 'Poi')[],
  ) {
    const { assets, cadFilesResponse } = await this.fetchDynamicAssets(projectId);
    await this.refreshDynamicAssets(assets, cadFilesResponse, types);
  }

  render(webGlRenderer: WebGLRenderer, css2DRenderer: CSS2DRenderer, camera: Camera) {
    /**
     * Render temp object first, this ensures that opacity will work when it is hidden behind
     * transaprent assets in the scene.
     */
    this.animateTempAssets(webGlRenderer, camera);
    SunManager.instance.renderObjectWithLightSources(webGlRenderer, this.tempObj, camera);
    SunManager.instance.renderObjectWithLightSources(webGlRenderer, this, camera);
    css2DRenderer.render(this, camera);
  }

  animateTempAssets(webGlRenderer: WebGLRenderer, camera: Camera) {
    for (const temp of this.temp) {
      if (temp instanceof OrthoTileAsset) {
        temp.update(camera, webGlRenderer);
      }
    }
  }

  renderText(renderer: CSS3DRenderer | undefined, camera: Camera) {
    if (!isDefined(renderer)) {
      return;
    }

    renderer.render(this.textScene, camera);
  }

  private async refreshTerrainModels(projectId: string) {
    if (this.sharedViewId) {
      return;
    }

    const terrainModels = (
      await this.AxiosProjectService.getTerrainModels({
        path: { projectId },
      })
    ).data;

    const timelines = ProjectGeodataManager.instance().getDetailedTimelineItems();

    for (const model of terrainModels) {
      const timeline = timelines.find((element) => {
        return model.geodataId === element.timelineContainer?.geodataId;
      });

      const terrainModel = new TerrainModel({
        timelineContainerId: timeline?.id || '',
        timelineContainerIndex: timeline?.number || -1,
        timelineContainerComment: timeline?.comment,
        geodataId: model.geodataId,
        cellGridVolumes: model.cellGridVolumes,
        prismVolumes: [],
        skyMapVolumeSourceIds: [],
      });

      // If the asset already exists in the asset manager,
      // and the new asset equals the existing, there is not need to replace the current asset.
      //
      // This ensures that geometry-heavy assets like CAD-files are not removed and reloaded
      // unnecessarily. Check 'equals' for comparison rules.
      const existingAsset = this.findChildAsset(terrainModel.assetId);
      if ((existingAsset as ElementObj)?.equals(terrainModel)) {
        disposeRecursive(terrainModel);
        return;
      }

      this.addAsset(terrainModel, {
        updateNative: false,
        replaceExisting: true,
        publishAssetListUpdatedEvent: false,
      });
    }
    return;
  }

  private async refreshStaticAssets(
    projectId: string,
    types?: (DynamicAssetType | 'Poi') | (DynamicAssetType | 'Poi')[],
  ) {
    const modelDataJSON = await this.fetchStaticAssets(projectId);

    if (isDefined(types) && !toArray(types).includes('Model' as never)) {
      return;
    }

    for (const staticAssetData of modelDataJSON) {
      const model = staticAssetData.model;

      const sceneModel = new Model(model);

      // If the asset already exists in the asset manager,
      // and the new asset equals the existing, there is not need to replace the current asset.
      //
      // This ensures that geometry-heavy assets like CAD-files are not removed and reloaded
      // unnecessarily. Check 'equals' for comparison rules.
      const existingAsset = this.findChildAsset(sceneModel.assetId);
      if ((existingAsset as ElementObj)?.equals(sceneModel)) {
        disposeRecursive(sceneModel);
        return;
      }
      this.addAsset(sceneModel, {
        updateNative: false,
        replaceExisting: true,
        publishAssetListUpdatedEvent: false,
      });

      sceneModel.setVisibilityChangedCallback(async (visible: boolean) => {
        if (visible && sceneModel.hasGeometry() === false) {
          try {
            sceneModel.loadingStatus = 'loading';

            const assetJsonResp = (
              await this.StaticAssetService.getModel({ body: { id: model.id } })
            ).data;
            if (assetJsonResp.gltf === null) {
              // No gltf data exists.
              sceneModel.loadingStatus = 'error';
              await sceneModel.setStateVisibility(false);
              publishNotification('info0511', { data: [model.name] });
            } else {
              // Fetch and parse model.
              const modelS3URL = (
                await this.StaticAssetService.getModelGeometry({ body: { id: model.id } })
              ).url;
              const geometry = await this.parseModelFromS3(modelS3URL);
              sceneModel.setGeometry(geometry);
            }

            sceneModel.loadingStatus = 'loaded';
          } catch (err) {
            sceneModel.loadingStatus = 'error';
            await sceneModel.setStateVisibility(false);
          }
        }
      });

      // Load model if current view is a shared view.
      if (this.sharedViewId && sceneModel.visible && sceneModel.assetType === 'model') {
        await sceneModel.setStateVisibility(true);
      }
    }
  }

  /**
   *  Load a projects assets. Define an optional types list of which assets to be loaded
   * @param project
   * @param types
   * @param callback
   */
  async loadProjectAssets(
    projectId: string,
    types?: (DynamicAssetType | 'Poi') | (DynamicAssetType | 'Poi')[],
  ) {
    this.loading = true;
    this.projectId = projectId;

    /// The asset manager checks the session display
    this.assetTypeVisibility = {
      dxf: true,
      poi: true,
      line: true,
      area: true,
      model: true,
      design: true,
      glb: true,
      'terrain-model': false,
    };

    AssetVisibilityManager.getInstance().reset();

    await Promise.all([
      this.refreshAllDynamicAssets(projectId, types),
      this.refreshStaticAssets(projectId, types),
      this.refreshTerrainModels(projectId),
    ]);

    this.publishAssetListChanged();

    this.pushAssets();
    this.loading = false;
  }

  removeDuplications(assetId?: string) {
    const duplicate = this.findChildAsset(assetId);

    if (duplicate) {
      // @ts-ignore
      duplicate.dispose?.();
      this.remove(duplicate);
    }
  }

  private findChildAsset(assetId: string | undefined) {
    return this.children.find((child: any) => {
      return child instanceof ElementObj && child.assetId === assetId;
    }) as Object3D | Mesh;
  }

  /**
   *  As we lazy load CAD-models and models
   *  we do not need to keep track of each single asset
   *  Instead, return one step for dynamic and static step respectively
   */
  getAssetLoadSteps() {
    return 2;
  }

  addAsset(
    asset: ElementObj,
    options: {
      updateNative: boolean;
      replaceExisting: boolean;
      publishAssetListUpdatedEvent: boolean;
    } = { updateNative: true, publishAssetListUpdatedEvent: true, replaceExisting: true },
  ) {
    const existingReplica = this.children.find((child) => {
      if (child instanceof TerrainModel || asset instanceof TerrainModel) {
        return (child as TerrainModel).geoDataId === (asset as TerrainModel).geoDataId;
      }

      // Only compare assetId when it's not undefined.
      // (All assets in CAD Viewer has assetId undefined)
      if (child instanceof ElementObj && child.assetId) {
        return child.assetId === asset.assetId;
      }

      return false;
    });

    // If we are not replacing existing, stop here.
    if (isDefined(existingReplica) && !options.replaceExisting) {
      return;
    }

    if (isDefined(existingReplica)) {
      this.remove(existingReplica);
      disposeRecursive(existingReplica);
    }
    this.add(asset);

    if (options.publishAssetListUpdatedEvent) {
      this.publishAssetListChanged();
    }

    if (options.updateNative) {
      this.pushAssets();
    }
  }

  publishAssetListChanged() {
    publish(SubscriptionTopic.AssetListChanged, { assets: this.children });
  }

  hasAsset(assetId: string) {
    return this.children.some((asset) => (asset as ElementObj).assetId === assetId);
  }

  async updateAsset(assetId: string, type: 'DynamicAsset' | 'Poi') {
    switch (type) {
      case 'DynamicAsset':
        await this.updateDynamicAsset(assetId);
        break;
      case 'Poi':
        await this.updatePoiAsset(assetId);
        break;
      default:
        assertNever(type);
    }
  }

  async updateTerrainModel(geoDataId: string) {
    const assetChild = this.children.find(
      (asset) => asset instanceof TerrainModel && asset.geoDataId === geoDataId,
    ) as TerrainModel;

    if (!assetChild) {
      return;
    }

    const assetData = (
      await SkyMapAxiosServiceFactory.instance.createTerrainModelServiceV0().fetchTerrainModel({
        path: { geodataId: geoDataId },
      })
    ).data;

    await this.refreshVolumeCalculations(
      assetChild,
      CellGridVolume.fromPropertyArray(assetData.cellGridVolumes),
      PrismVolume.fromPropertyArray([]),
    );
  }

  async refreshVolumeCalculations(
    asset: VolumeCalculationAsset,
    cellGridVolumes: CellGridVolume[],
    prismVolumes: PrismVolume[],
  ) {
    const updatedVolume =
      cellGridVolumes.find((newVolume) =>
        asset.cellGridVolumes.find(
          (oldVolume: CellGridVolume) =>
            oldVolume.id === newVolume.id && oldVolume.status !== newVolume.status,
        ),
      ) ??
      prismVolumes.find((newVolume) =>
        asset.prismVolumes.find(
          (oldVolume: PrismVolume) =>
            oldVolume.id === newVolume.id && oldVolume.status !== newVolume.status,
        ),
      );

    if (updatedVolume && !updatedVolume.isProcessing()) {
      if (updatedVolume instanceof CellGridVolume) {
        asset.cellGridVolumes = cellGridVolumes;
      } else {
        asset.prismVolumes = prismVolumes;
      }

      asset.volumeHandler.activeVolume = updatedVolume;

      const data = [
        updatedVolume.name ?? translateLiteral('skyviewVolumeCalculation.defaultName'),
        getAssetVolumeCalculationDataType(asset).toString(),
        asset.name,
      ];

      if (asset.volumeHandler.getStatus() === 'COMPLETED') {
        publishNotification('succ1101', { data });
      } else if (asset.volumeHandler.getStatus() === 'FAILED') {
        publishNotification('err1101', { data });
      }
      await asset.volumeHandler.refreshVolume();
      publish(SubscriptionTopic.AssetChanged, asset);
    }
  }

  calculatePositions(items: Object3D[]) {
    for (const child of items) {
      if (child instanceof ElementObj) {
        const newPos = child.calculateLocalPosition();
        const newHeight = child.calculateLocalHeight();

        if (newPos) {
          child.position.set(newPos.x, newPos.y, newHeight || child.position.z);
        }
      } else if (child instanceof CellGridVolumeModel || child instanceof PrismVolumeModel) {
        child.refreshLocalPosition();
      }

      // Update matrix world after tansforming objects so functions like localToWorld and worldToLocal will
      // function as expected.
      child.updateMatrixWorld(true);
    }
  }

  isLoaded() {
    const { project } = projectStateAdapter.getSnapshot();
    return isDefined(this.projectId) && project?.id === this.projectId;
  }

  isLoading() {
    return this.loading;
  }

  addTemp(temp: Object3D) {
    this.temp.push(temp);
    this.tempObj.add(temp);

    return temp;
  }

  removeTemp(temp: Object3D, options?: { dispose: boolean }) {
    const idx = this.temp.indexOf(temp);
    if (idx > -1) {
      this.temp.splice(idx, 1);

      if (options?.dispose) {
        disposeRecursive(temp);
      }

      this.tempObj.remove(temp);
    }
  }

  /**
   * @description Returns an asset matching uuid
   * @param {string} uuid
   */
  getAsset(uuid?: string) {
    return this.children.find((child) => child.uuid === uuid) as ElementObj | undefined;
  }

  getAssetByAssetId(assetId: string) {
    return this.getAssets().find((x) => x.assetId === assetId);
  }

  getAssets(includePoi = false) {
    return this.children.reduce((prev: ElementObj[], child: Object3D) => {
      // Filter out Clusters, as they are not actual assets,
      // but just representations of Poi clusters.
      if (!includePoi && child instanceof Cluster) {
        return prev;
      }
      if (child instanceof ElementObj) {
        prev.push(child);
      }
      return prev;
    }, []);
  }

  getAssetsByType<T extends AssetType>(assetType: T): AssetTypeToModel<T> {
    return this.filterAssetsByType(this.getAssets(), assetType);
  }

  filterAssetsByType<T extends AssetType>(assets: ElementObj[], assetType: T): AssetTypeToModel<T> {
    return assets.filter((asset) => {
      switch (assetType) {
        case 'area':
          return asset instanceof Geometry && asset.isArea;
        case 'line':
          return asset instanceof Geometry && asset.isArea === false;
        case 'design':
          return asset instanceof Design;
        case 'poi':
          return asset instanceof Poi;
        case 'dxf':
          return asset instanceof Dxf;
        case 'glb':
          return asset instanceof Glb;
        case 'model':
          return asset instanceof Model;
        case 'terrain-model':
          return asset instanceof TerrainModel;
        default:
          assertNever(assetType);
      }
    }) as AssetTypeToModel<T>;
  }

  getAssetsByCategory(assetType: AssetCategory) {
    return this.getAssets().filter((asset) => {
      switch (assetType) {
        case 'area':
          return asset instanceof Geometry && asset.isArea;
        case 'line':
          return asset instanceof Geometry && asset.isArea === false;
        case 'design':
          return asset instanceof Design;
        case 'poi':
          return asset instanceof Poi;
        case 'cad':
          return asset instanceof Dxf || asset instanceof Glb;
        case 'model':
          return asset instanceof Model;
        case 'terrain-model':
          return asset instanceof TerrainModel;

        default:
          return false;
      }
    });
  }

  /**
   * Gets all completed cell grid and prism volume calculations in the project.
   */
  getCompletedVolumeCalculations(): VolumeWithAsset[] {
    return this.getAssets().flatMap(getCompletedVolumes);
  }

  selectByUuid(uuid: string) {
    this.select(this.getAsset(uuid));
  }

  select(selection?: ElementObj) {
    this.unselect();
    this.setSelected(selection);

    (window as any)?.iosCallbacks?.pushAssetSelection(selection?.uuid || '');
  }

  onAnimate(camera?: SkyviewCamera) {
    const threeCamera = camera?.getTHREECamera();
    threeCamera?.updateMatrix(); // make sure camera's local matrix is updated
    threeCamera?.updateMatrixWorld(); // make sure camera's world matrix is updated

    this.children.map((child: any) => {
      child?.onAnimate?.(camera);
    });
  }

  pick(x: number, y: number, camera: Camera): ElementObj | undefined {
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(x, y), camera);

    let intersectTarget: ElementObj | undefined;
    let closestIntersection;

    for (const child of this.children) {
      if (child.visible && child instanceof ElementObj) {
        const distance = child.position.distanceTo(camera.position);
        raycaster.params.Line.threshold = Math.min(1.0, distance / 500);
        const intersectResults = raycaster.intersectObject(child, true);

        for (const intersection of intersectResults) {
          intersection.distanceToRay = raycaster.ray.distanceToPoint(intersection.point);
        }

        if (intersectResults.length > 0) {
          const intersection = intersectResults.sort(
            (a, b) => a.distanceToRay! - b.distanceToRay!,
          )[0];
          let replace = true;

          if (closestIntersection !== undefined) {
            const difference = Math.abs(
              intersection.distanceToRay! - closestIntersection.distanceToRay!,
            );
            replace = false;

            if (difference < 0.1) {
              // Always prioritize picking POIs even if they are behind other objects.
              if (child instanceof Poi) {
                replace = true;
              } else if (closestIntersection.distanceToRay === 0) {
                // If hard to pick object like a line is infront of a surface. pick the one that is not right on the raycast.
                if (intersection.distanceToRay !== 0) {
                  replace = true;
                }
                // If both objects are right on the raycast, pick the one closest to the camera.
                else if (intersection.distance < closestIntersection.distance) {
                  replace = true;
                }
              }
            }
            // Default picking, pick the one closest to raycast.
            else if (intersection.distanceToRay! < closestIntersection.distanceToRay!) {
              replace = true;
            }
          }

          if (replace) {
            intersectTarget = child;
            closestIntersection = intersection;
          }
        }
      }
    }

    return intersectTarget;
  }

  setSelected(selected?: ElementObj) {
    this.selected = selected;

    if (!selected) {
      PubSub.publish('unSelection', undefined);
      return;
    }

    PubSub.publish('newSelection', this.selected);

    selected.onSelect();

    InputHandler.instance.currentTool()?.onNewAssetSelection(selected);
    this.viewer?.skyview?.dispatchEvent({ type: 'updateUI' });
    this.viewer?.skyview?.dispatchEvent({ type: 'animate' });
  }

  getSelected() {
    return this.selected;
  }

  getSelectedData() {
    return (this.selected as any)?.getData?.();
  }

  getSelectedMeta() {
    return (this.selected as any)?.getMeta?.();
  }

  getSelectedReadMeta() {
    return (this.selected as any)?.getReadMeta?.();
  }

  /**
   *  @deprecated Only referenced for the native application library for native apps, which is no longer in use.
   *  Accesses stateVisibility directly, and does not update the asset manager's assetTypeVisibility map.
   */
  setAssetTypeVisibility(type: AssetType, state: boolean) {
    // Accessed stateVisibility directly. Removed from implementation.
    throw new Error('Not implemented. No longer in use.');
  }

  getAssetTypeVisibility(type?: AssetType) {
    if (!type) {
      return this.assetTypeVisibility;
    }

    return this.assetTypeVisibility[type] === undefined
      ? this.assetTypeVisibility
      : this.assetTypeVisibility[type];
  }

  unselect() {
    if (this.selected) {
      this.selected.onDeselect();
    }
  }

  async createDynamicAssetFromDocument(
    docName: string,
    docPath: string,
    file: FileModel,
    fileId: string,
  ) {
    const options = {
      name: docName,
      fileId,
      fileInfo: {
        ...file,
        path: docPath,
      },
      visible: false,
    };

    switch (getFileExtension(docName).toLowerCase()) {
      case '.glb':
      case '.xml':
      case '.geo':
      case '.ifc':
      case '.kml':
      case '.kmz':
      case '.shp':
      case '.shape':
      case '.json':
      case '.geojson':
      case '.zip':
      case '.dae':
      case '.pdf':
      case '.png':
      case '.jpg':
      case '.jpeg':
        return (await this.saveCadAssetData(new Glb(undefined, this.sharedViewId, options))).data;
      case '.dxf':
      case '.dwg':
        // The backend converts drawings both to a glb and to the DXF that the legacy DXF asset
        // parses. With the flag, a drawing is a glb asset, like PDFs and IFC models, whose
        // versions are converted before it is added, see convertDrawingFiles.
        if (FeatureFlagsManager.instance.isEnabled('SK-7537-dwg-to-glb')) {
          return (await this.saveCadAssetData(new Glb(undefined, this.sharedViewId, options))).data;
        }

        return (
          await this.saveCadAssetData(new Dxf(undefined, options, undefined, this.sharedViewId))
        ).data;
    }
  }

  /**
   * Converts all versions of the DWG and DXF files that are added to SkyView to glb (with the
   * DWG to glb flag, when they are glb assets), as PDFs are converted when they are added, so that
   * every version can be shown: versions converted the legacy way (to a DXF) get a glb next to
   * their DXF. One request converts the versions of all files, at most 100 at a time. The backend
   * skips versions that already have a glb or are being converted, and sends DynamicAssetUpdated
   * to the file's assets when a conversion has finished.
   *
   * Call it before the assets are saved, so that an asset is never read before its versions are
   * converting. A version that is not converted can't be shown, and the asset tells the user to
   * add the file again, see CadBody.
   *
   * @returns The files whose conversion could not be requested, which should not be added yet:
   * all requests are made, also after one that fails.
   */
  async convertDrawingFiles<T extends { name: string; versions: { id: string }[] }>(
    files: T[],
  ): Promise<T[]> {
    if (!FeatureFlagsManager.instance.isEnabled('SK-7537-dwg-to-glb')) {
      return [];
    }

    const drawings = files.filter((x) =>
      ['.dwg', '.dxf'].includes(getFileExtension(x.name).toLowerCase()),
    );
    const fileVersionIds = drawings.flatMap((x) => x.versions.map((version) => version.id));
    const failedFileVersionIds = new Set<string>();
    const service = SkyMapAxiosServiceFactory.instance.createFileVersionServiceInternal();

    for (let i = 0; i < fileVersionIds.length; i += maxConvertedFileVersions) {
      const batch = fileVersionIds.slice(i, i + maxConvertedFileVersions);

      try {
        await service.convertFileVersions({ body: { fileVersionIds: batch } });
      } catch (err) {
        console.log('Error: Failed to convert drawing versions:', JSON.stringify(err));
        batch.forEach((id) => failedFileVersionIds.add(id));
      }
    }

    return drawings.filter((x) =>
      x.versions.some((version) => failedFileVersionIds.has(version.id)),
    );
  }

  async saveCadAssetData(asset: AssetWithFileInfo) {
    const assetData = await asset.getSaveData();
    const { project } = projectStateAdapter.getSnapshot();
    const activeTimelineContainer = ProjectGeodataManager.instance().getActiveTimelineContainer();

    assetData.projectId = project!.id;
    if (activeTimelineContainer) {
      assetData.timelineContainerId = activeTimelineContainer.id;
    }

    return await SkyMapAxiosServiceFactory.instance
      .createDynamicAssetServiceV0()
      .saveDynamicAsset({ body: assetData });
  }

  async save(asset?: ElementObj) {
    asset = asset || this.selected;
    if (!asset) {
      throw new Error('There is no asset to save.');
    }

    if (this.readOnly) {
      return;
    }

    const newlyCreated = asset.assetId === undefined;

    if (asset instanceof ElementObj) {
      const assetData = await (asset as any).getSaveData();
      if (newlyCreated) {
        const { project } = projectStateAdapter.getSnapshot();
        assetData.projectId = project!.id;

        const timelineContainerId =
          ProjectGeodataManager.instance().getActiveTimelineContainer()?.id;
        if (timelineContainerId) {
          assetData.timelineContainerId = timelineContainerId;
        }
      }

      try {
        asset.saveState = AssetSaveState.Saving;

        if (asset instanceof Model) {
          const result = await this.StaticAssetService.save({ body: assetData });
          asset.onSaved?.(result.data);
        } else if (asset instanceof Poi) {
          await SkyMapAxiosServiceFactory.instance.createPoiServiceV1().patchPoi({
            body: assetData as PoiPatchPayload,
            path: {
              poiId: asset.assetId!,
            },
            query: {},
          });

          const { data: updatedPoi } = await SkyMapAxiosServiceFactory.instance
            .createPoiServiceV1()
            .fetchPoi({
              path: { poiId: asset.assetId! },
              query: {
                include: [
                  'poi.thumbnailImage',
                  'poi.timelineContainer',
                  'poi.createdBy',
                  'poi.updatedBy',
                ],
              },
            });

          asset.onSaved({
            ...updatedPoi,
          } satisfies PoiProperties);
        } else {
          const result = await SkyMapAxiosServiceFactory.instance
            .createDynamicAssetServiceV0()
            .saveDynamicAsset({ body: assetData });
          (asset as any)?.onSaved?.(result.data);
        }

        this.pushAssets();

        publish(SubscriptionTopic.AssetChanged, asset);
        asset.saveState = AssetSaveState.Idle;
      } catch (err) {
        asset.saveState = AssetSaveState.Error;
        throw err;
      }
    }
  }

  async delete(asset?: ElementObj, deleteFromProject = true) {
    asset = asset || this.selected;
    if (!asset) {
      throw new Error('There is no asset to remove.');
    }

    if (asset.assetId && deleteFromProject) {
      const isPoiAsset = asset.assetType === 'poi';
      if (isPoiAsset) {
        await SkyMapAxiosServiceFactory.instance
          .createPoiServiceV1()
          .deletePoi({ path: { poiId: asset.assetId } });
      } else {
        await SkyMapAxiosServiceFactory.instance
          .createDynamicAssetServiceV0()
          .delete({ body: { id: asset.assetId } });
      }
    }

    (asset as any)?.dispose?.();
    this.remove(asset);

    this.pushAssets();

    // Reset the selection
    this.select(undefined);

    publish(SubscriptionTopic.AssetListChanged);
  }

  clearCustom() {
    this.setSelected(undefined);

    while (this.children.length) {
      (this.children[0] as any)?.dispose?.();
      this.remove(this.children[0]);
    }
  }

  ////
  // Native IoS callback
  pushAssets() {
    (window as any)?.iosCallbacks?.pushAssets?.(this.getAssets(), this.assetTypeVisibility);
  }

  parseModelFromS3 = async (modelS3URL: string): Promise<THREE.Object3D> => {
    const modelGeometry = await WebApi.fetchS3DataAsArrayBuffer(modelS3URL);

    const parsedGltf = await GltfLoader.instance.loadGltf(Buffer.from(modelGeometry));

    const geometryChild = parsedGltf.scene.children[0].children[0];
    geometryChild.matrixAutoUpdate = true;

    return geometryChild;
  };

  /**
   * Removes those cad files from scene where asset is not in the specified list of cad files.
   */
  private removeDeletedCadFiles(cadFiles: CadFile[]) {
    const existingCadAssets = this.children.filter(
      (child) => child instanceof Dxf || child instanceof Glb,
    ) as (Dxf | Glb)[];

    const assetsToRemove = existingCadAssets.filter(
      (asset) => !cadFiles.some((x) => x.file.id === asset.fileId),
    );

    this.remove(...assetsToRemove);
  }

  /**
   * Returns true if last refresh av cad file has a path that is different from the current path.
   * Once this function is called, the previous path is set to the path of the specified cad file.
   */
  private hasCadFilePathChangedSinceLastRefresh(cadFile?: CadFile) {
    if (cadFile) {
      const prevValue = this.prevCadAssetPathMap.get(cadFile.file.id);
      this.prevCadAssetPathMap.set(cadFile.file.id, cadFile?.path);
      return prevValue !== cadFile.path;
    }

    return false;
  }

  private async refreshDynamicAssets(
    assetData: (DynamicAssetModel | PoiModel)[],
    cadFilesResult: GetCadFilesResponse,
    types?: (DynamicAssetType | 'Poi') | (DynamicAssetType | 'Poi')[],
  ) {
    // When refreshDynamicAssets is called and some assets no longer exists on the server, remove
    // these assets from the scene.
    this.removeDeletedCadFiles(cadFilesResult.data);

    const filterOnTypes = toArray(types);
    const filteredAssets = isDefined(types)
      ? assetData.filter((asset) =>
          filterOnTypes.includes((asset as DynamicAssetModel).type ?? 'Poi'),
        )
      : assetData;

    for (const asset of filteredAssets) {
      const isDynamicAsset = isDefined((asset as DynamicAssetModel).properties);
      /**
       * Note:
       * Force cast "visible" to boolean since it's saved as a string in the json in DB
       * for some older assets.
       */
      if (isDynamicAsset) {
        (asset as DynamicAssetModel).properties.visible =
          (asset as DynamicAssetModel).properties.visible?.toString() === 'true';
      }

      const cadFile = cadFilesResult.data.find(
        (f) => f.file?.id === (asset as DynamicAssetModel).fileId,
      );

      const assetElement = AssetFactory.instance.buildAsset(asset, this.sharedViewId, cadFile);
      if (!assetElement) {
        continue;
      }

      // If the asset already exists in the asset manager,
      // and the new asset equals the existing, there is not need to replace the current asset.
      //
      // This ensures that geometry-heavy assets like CAD-files are not removed and reloaded
      // unnecessarily. Check 'equals' for comparison rules.
      //
      // Because folders can be moved to other folders, we need to check if the path of the cad file
      // has changed in order to determine whether to reload the asset (path is not included in
      // asset equality).
      const existingAsset = this.findChildAsset(assetElement.assetId);
      const isCurrentAssetEqualToNew = (existingAsset as ElementObj)?.equals(assetElement);
      if (isCurrentAssetEqualToNew && !this.hasCadFilePathChangedSinceLastRefresh(cadFile)) {
        disposeRecursive(assetElement);
        continue;
      }

      // If the previous asset was visible, ensure the new one is as well.
      // This will trigger mesh assets (Dxf, TerrainModel, etc.) to re-fetch and re-process
      // its data.
      if (existingAsset?.visible) {
        await assetElement.setStateVisibility(true);
      }

      // In shared view mode, only force-load DXF/GLB assets that were saved as visible.
      // CAD files store properties.visible=false by default; geometry only fetches when
      // setStateVisibility transitions false→true. Assets saved as hidden stay unloaded
      // until the user shows them manually, avoiding unnecessary heavy geometry fetches.
      const isCadAsset = assetElement.assetType === 'dxf' || assetElement.assetType === 'glb';
      if (this.sharedViewId && isDynamicAsset && isCadAsset) {
        if (this.sharedViewAssetVisibilities.get(assetElement.assetId ?? '') !== false) {
          await assetElement.setStateVisibility(true);
        }
      }

      this.addAsset(assetElement, {
        updateNative: false,
        replaceExisting: true,
        publishAssetListUpdatedEvent: false,
      });
    }
  }

  private async updateDynamicAsset(assetId: string) {
    try {
      const assetData = (
        await SkyMapAxiosServiceFactory.instance.createDynamicAssetServiceV0().getDynamicAsset({
          path: { id: assetId },
          query: {
            include: [
              'dynamicAsset.file',
              'dynamicAsset.file.versions',
              'dynamicAsset.file.versions.glb',
              'dynamicAsset.pdfs',
              'dynamicAsset.assetTags',
            ],
          },
        })
      ).data;

      if (!this.hasAsset(assetId)) {
        const assetElement = AssetFactory.instance.buildAsset(
          assetData,
          this.sharedViewId,
          isDefined(assetData.file) && isDefined(assetData.filePath)
            ? {
                path: assetData.filePath,
                file: assetData.file,
              }
            : undefined,
        );

        if (!assetElement) {
          return;
        }

        this.addAsset(assetElement, {
          updateNative: false,
          replaceExisting: false,
          publishAssetListUpdatedEvent: false,
        });
      }

      const assetChild = (this.children as ElementObj[]).find((asset) => asset.assetId === assetId);

      if (!isDefined(assetChild)) {
        return;
      }

      // Refresh cellgrid volumes.
      if (VolumeCalculationAssetTypeGuard.check(assetChild)) {
        await this.refreshVolumeCalculations(
          assetChild,
          CellGridVolume.fromPropertyArray(
            (assetData.properties as DynamicAssetCommonPropertiesModel).cellGridVolumes,
          ),
          PrismVolume.fromPropertyArray(assetData.targetPrismVolumes),
        );
      }

      // Sync file info with the existing asset.
      if (assetChild instanceof Dxf || assetChild instanceof Glb) {
        if (isDefined(assetData.file) && isDefined(assetData.filePath)) {
          assetChild.fileInfo = {
            ...assetData.file,
            path: assetData.filePath,
          };
        }
      }

      // Sync PDF:s with existing asset.
      if (assetChild instanceof Glb) {
        assetChild.pdfs = assetData.pdfs ?? [];
      }
      assetChild.assetTagIds = assetData.assetTags?.map((tag) => tag.id) ?? [];

      this.publishAssetListChanged();
      await assetChild.onChange();
    } catch (err) {
      console.log('Error: Failed to update asset:', JSON.stringify(err));
    }
  }

  private async updatePoiAsset(poiId: string) {
    const poiService = SkyMapAxiosServiceFactory.instance.createPoiServiceV1();

    const { data: updatedPoi } = await poiService.fetchPoi({
      path: { poiId },
      query: {
        include: [
          'poi.createdBy',
          'poi.thumbnailImage',
          'poi.timelineContainer',
          'poi.updatedBy',
          'poi.assetTags',
        ],
      },
    });

    const newPoi = AssetFactory.instance.buildAsset(updatedPoi, undefined);
    if (!isDefined(newPoi)) {
      return;
    }

    const existingAsset = this.getAssetByAssetId(updatedPoi.id);
    if (!isDefined(existingAsset)) {
      this.addAsset(newPoi, {
        updateNative: false,
        replaceExisting: false,
        publishAssetListUpdatedEvent: false,
      });
    } else {
      existingAsset.patch(newPoi);
    }
  }
}
